import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { useT } from '../lib/i18n';
import { normalizeRichText } from '../lib/richText';
import { buildEmbedUrl } from './AurexSections';

// ─────────────────────────────────────────────────────────────────────────
// ModernVideo — the «Moderno» theme Video section (s139, approved by Anthony:
// "Full-bleed Poster"). The poster fills the section with a big play button;
// the video plays in place, full width. Click-to-play only: nothing loads,
// plays or makes sound until the visitor presses play ("Autoplay" is ignored).
// Uses the aurex_video data: eyebrow/title/subtitle/video_url/poster_url/aspect_ratio.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const CSS = `
.mv-play{transition:transform 180ms cubic-bezier(0.23,1,0.32,1)}
.mv-play:active{transform:scale(.96)}
@media (hover:hover) and (pointer:fine){.mv-trigger:hover .mv-play{transform:scale(1.06)}}
.mv-trigger:focus-visible{outline:3px solid var(--color-accent,#ff9c1b);outline-offset:4px}
.mv-title em{font-style:italic;font-weight:500;color:var(--accent-strong,#b86500)}
.mv-dark .mv-title em{color:var(--color-accent,#ff9c1b)}
`;

// Player: iframe (YouTube/Vimeo) or <video> (.mp4/.webm/.ogg), mounted only after a click.
function Player({ url, title }) {
  const embed = buildEmbedUrl(url, true);
  const isDirect = !buildEmbedUrl(url, false) && /\.(mp4|webm|ogg)$/i.test(url || '');
  if (isDirect) return <video src={url} controls autoPlay playsInline className="absolute inset-0 w-full h-full object-cover" />;
  if (!embed) return null;
  // Mounted only after the click → autoplay WITH sound (drop mute/loop from buildEmbedUrl).
  const src = embed.replace(/([?&])mute(d)?=1&?/, '$1').replace(/([?&])loop=1&?/, '$1').replace(/([?&])playlist=[^&]*&?/, '$1').replace(/[?&]$/, '');
  return <iframe src={src} className="absolute inset-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={title || 'Video'} />;
}

function PlayButton({ size = 80 }) {
  return (
    <span className="mv-play grid place-items-center rounded-full" style={{ width: size, height: size, background: 'var(--color-accent, #ff9c1b)', color: 'var(--accent-ink, #0f172a)', boxShadow: '0 16px 40px -14px rgba(0,0,0,.6)' }}>
      <Play style={{ width: size * 0.34, height: size * 0.34, marginLeft: size * 0.05 }} fill="currentColor" />
    </span>
  );
}

function Eyebrow({ text, center, dark }) {
  if (!text) return null;
  const c = dark ? 'var(--color-accent, #ff9c1b)' : 'var(--accent-strong, #b86500)';
  return (
    <div className={`inline-flex items-center gap-3 text-[11px] font-bold uppercase ${center ? 'justify-center' : ''}`} style={{ letterSpacing: '.2em', color: c }} data-testid="aurex-video-eyebrow">
      <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />
      <span dangerouslySetInnerHTML={{ __html: normalizeRichText(text) }} />
    </div>
  );
}

function FullBleedPoster({ config }) {
  const tt = useT();
  const [playing, setPlaying] = useState(false);
  const title = tt(config.title);
  const subtitle = tt(config.subtitle);

  if (playing) {
    return (
      <section id="aurex-video" data-testid="aurex-section-video" className="relative bg-black">
        <style>{CSS}</style>
        <div className="relative w-full" style={{ aspectRatio: config.aspect_ratio || '16/9', maxHeight: '90vh', margin: '0 auto' }}>
          <Player url={config.video_url} title={title} />
        </div>
      </section>
    );
  }

  return (
    <section id="aurex-video" data-testid="aurex-section-video" className="mv-dark relative overflow-hidden bg-black min-h-[560px] lg:min-h-[680px] flex items-end">
      <style>{CSS}</style>
      {config.poster_url
        ? <img src={config.poster_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        : <span className="absolute inset-0" style={{ background: 'linear-gradient(135deg,#2a2d33,#131417)' }} />}
      <div className="absolute inset-0 hidden md:block" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,.74) 0%, rgba(0,0,0,.3) 55%, rgba(0,0,0,.1) 100%)' }} />
      <div className="absolute inset-0 md:hidden" style={{ background: 'linear-gradient(0deg, rgba(0,0,0,.82) 0%, rgba(0,0,0,.25) 65%)' }} />

      <button type="button" onClick={() => setPlaying(true)} className="mv-trigger absolute z-20 left-1/2 top-[34%] -translate-x-1/2 -translate-y-1/2 md:left-auto md:translate-x-0 md:right-[12%] md:top-1/2 grid place-items-center" aria-label="Play video" data-testid="modern-video-play">
        <span className="absolute rounded-full border pointer-events-none" style={{ inset: -18, borderColor: 'rgba(255,255,255,.45)' }} />
        <PlayButton size={104} />
      </button>

      {/* copy layer spans the full width — let clicks through to the play button */}
      <div className="relative z-10 w-full px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] pb-14 lg:pb-20 pt-40 pointer-events-none">
        <div className="flex flex-col gap-4 max-w-xl pointer-events-auto" style={{ textShadow: '0 2px 20px rgba(0,0,0,.3)' }}>
          <Eyebrow text={tt(config.eyebrow)} dark />
          {title && <h2 className="mv-title font-bold text-white" style={{ fontFamily: SERIF, fontSize: 'clamp(2.2rem, 4.4vw, 3.75rem)', lineHeight: 1.05, textWrap: 'balance', color: '#ffffff' }} data-testid="aurex-video-title" dangerouslySetInnerHTML={{ __html: normalizeRichText(title) }} />}
          {subtitle && <div className="text-base md:text-lg leading-relaxed" style={{ color: 'rgba(255,255,255,.82)' }} dangerouslySetInnerHTML={{ __html: normalizeRichText(subtitle) }} />}
        </div>
      </div>
    </section>
  );
}

export default function ModernVideo({ config = {} }) {
  if (!config.video_url) return null;
  return <FullBleedPoster config={config} />;
}
